import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  notificationsApi,
  CreateNotificationDto,
  NotificationType,
  AudienceType,
  type AudienceFilters,
  type PreviewNotificationResponse,
} from '../../api/notifications';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { ArrowLeft, Loader2, Send, CalendarClock, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../../components/shared/ConfirmDialog';
import { ErrorState, FormField, PageHeader, SegmentedControl } from '@/components/app';
import { BRAND } from '@/lib/brand';
import { getErrorMessage } from '@/lib/errors';
import { NOTIFICATION_TYPE_LABELS } from '@/lib/labels';
import { fromTashkentInput, formatTashkentTime, toTashkentInput } from '@/lib/datetime';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { cn } from '@/lib/utils';

const fmt = (n: number) => n.toLocaleString('ru-RU');

/**
 * Экраны, которые приложение умеет открыть по диплинку `<scheme>://<Экран>`.
 * Имена — маршруты RootStackParamList из mobile/src/navigation/AppNavigator.tsx;
 * сюда попадают только экраны без обязательных параметров (NotificationService
 * передаёт в navigate() лишь query-параметры ссылки). Бэкенд кладёт `data` как есть
 * и ссылку не проверяет, поэтому произвольный текст не нужен — опечатка в имени
 * экрана молча ничего не открывала бы.
 */
const DEEP_LINK_SCREENS: Array<{ value: string; label: string }> = [
  { value: 'MainTabs', label: 'Главная' },
  { value: 'MockExamInfo', label: 'Пробный экзамен' },
  { value: 'TicketsList', label: 'Билеты' },
  { value: 'Topics', label: 'Темы' },
  { value: 'MarathonInfo', label: 'Марафон' },
  { value: 'WrongExamInfo', label: 'Работа над ошибками' },
  { value: 'WeakSpots', label: 'Слабые места' },
  { value: 'FavouriteQuestions', label: 'Избранные вопросы' },
  { value: 'TrickyQuestions', label: 'Каверзные вопросы' },
  { value: 'VideoLessons', label: 'Видеоуроки' },
  { value: 'Fines', label: 'Штрафы' },
  { value: 'DailyChallenge', label: 'Вызов дня' },
  { value: 'TimeAttack', label: 'Тайм-атака (Spidran)' },
  { value: 'Survival', label: 'Выживание (Hayot)' },
  { value: 'Duel', label: 'Дуэль' },
  { value: 'Leaderboard', label: 'Лидерборд' },
  { value: 'Payment', label: 'Оплата Premium' },
  { value: 'SubscriptionsHistory', label: 'История подписок' },
  { value: 'NotificationsList', label: 'Список уведомлений' },
  { value: 'NotificationSettings', label: 'Настройки уведомлений' },
  { value: 'Profile', label: 'Профиль' },
  { value: 'Settings', label: 'Настройки' },
];

const TYPE_OPTIONS = Object.values(NotificationType).map((t) => ({ value: t, label: NOTIFICATION_TYPE_LABELS[t] }));

/** Подписи типа в приложении (mobile/src/locales/translations.ts, notifications.types.*, uz). */
const APP_TYPE_LABELS_UZ: Record<NotificationType, string> = {
  [NotificationType.SYSTEM]: 'Tizim',
  [NotificationType.PAYMENT]: 'To‘lov',
  [NotificationType.SUBSCRIPTION]: 'Obuna',
  [NotificationType.PROMO]: 'Promo',
  [NotificationType.REMINDER]: 'Eslatma',
  [NotificationType.ACHIEVEMENT]: 'Yutuq',
};
/** notifications.openRelatedContent (uz) — кнопка карточки, если есть диплинк. */
const APP_OPEN_LABEL_UZ = 'Bog‘liq kontentni ochish';

const AUDIENCES: Array<{ value: AudienceType; label: string; hint?: string }> = [
  { value: AudienceType.ALL, label: 'Все' },
  { value: AudienceType.FREE, label: 'Free' },
  { value: AudienceType.PREMIUM, label: 'Premium' },
  { value: AudienceType.BY_PREFERENCES, label: 'По настройкам уведомлений', hint: 'Только те, у кого включён этот тип' },
  { value: AudienceType.BY_ACTIVITY, label: 'По активности' },
];


export default function CreateNotificationPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<PreviewNotificationResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewAt, setPreviewAt] = useState<Date | null>(null);
  const [sendNow, setSendNow] = useState(true);
  const [scheduledAt, setScheduledAt] = useState('');
  const [deepLinkScreen, setDeepLinkScreen] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState<CreateNotificationDto>({
    title: '',
    body: '',
    type: NotificationType.SYSTEM,
    data: {},
    audience_filters: { audience_type: AudienceType.ALL },
    priority: 5,
    is_sponsored: false,
    is_premium: false,
  });

  const audience = formData.audience_filters ?? { audience_type: AudienceType.ALL };
  const deepLink = deepLinkScreen ? `${BRAND.scheme}://${deepLinkScreen}` : '';
  const setAudience = (patch: Partial<AudienceFilters>) =>
    setFormData((prev) => ({ ...prev, audience_filters: { ...audience, ...prev.audience_filters, ...patch } }));
  const setActivity = (key: 'days_active' | 'days_inactive', raw: string) =>
    setFormData((prev) => ({
      ...prev,
      audience_filters: {
        ...prev.audience_filters!,
        activity_filters: { ...prev.audience_filters?.activity_filters, [key]: raw ? parseInt(raw, 10) : undefined },
      },
    }));

  // Охват пересчитываем сами при смене аудитории/типа: число получателей не зависит от текста.
  const runPreview = async (type: NotificationType, filters: AudienceFilters | undefined) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const data = await notificationsApi.preview({ title: formData.title, body: formData.body, type, audience_filters: filters });
      setPreview(data);
      setPreviewAt(new Date());
    } catch (error: unknown) {
      setPreviewError(getErrorMessage(error, 'Не удалось рассчитать охват'));
    } finally {
      setPreviewLoading(false);
    }
  };
  const schedulePreview = useDebouncedCallback(runPreview, 400);
  const filtersKey = JSON.stringify(formData.audience_filters);
  useEffect(() => {
    setPreviewLoading(true);
    schedulePreview(formData.type, formData.audience_filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type, filtersKey]);

  const titleError = submitted && !formData.title.trim() ? 'Введите заголовок' : undefined;
  const bodyError = submitted && !formData.body.trim() ? 'Введите текст' : undefined;
  const scheduleIso = sendNow ? null : fromTashkentInput(scheduledAt);
  const scheduleError =
    submitted && !sendNow
      ? !scheduleIso
        ? 'Укажите дату и время отправки'
        : new Date(scheduleIso).getTime() <= Date.now()
          ? 'Время отправки должно быть в будущем'
          : undefined
      : undefined;

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error('Заполните заголовок и текст');
      return;
    }
    let scheduled: string | undefined;
    if (!sendNow) {
      if (!scheduleIso) {
        toast.error('Укажите дату и время отправки');
        return;
      }
      if (new Date(scheduleIso).getTime() <= Date.now()) {
        toast.error('Время отправки должно быть в будущем');
        return;
      }
      scheduled = scheduleIso;
    }

    const recipients = preview?.estimated_recipients;
    const pushRecipients = preview?.estimated_push_recipients;
    const isAll = audience.audience_type === AudienceType.ALL;
    const ok = await confirm({
      title: sendNow
        ? isAll ? 'Отправить уведомление всем?' : 'Отправить уведомление?'
        : isAll ? 'Запланировать рассылку всем?' : 'Запланировать рассылку?',
      description:
        (recipients != null
          ? `Сообщение увидят ~${fmt(recipients)} чел., push уйдёт ~${fmt(pushRecipients ?? 0)} чел.`
          : isAll ? 'Уведомление получат все пользователи.' : 'Уведомление получит выбранная аудитория.') +
        (sendNow ? ' Действие нельзя отменить после отправки.' : ` Отправка: ${scheduledAt.replace('T', ' ')} по Ташкенту.`),
      confirmLabel: sendNow ? 'Отправить' : 'Запланировать',
    });
    if (!ok) return;

    setIsLoading(true);
    try {
      const submitData: CreateNotificationDto = {
        ...formData,
        data: deepLink ? { ...formData.data, deep_link: deepLink } : formData.data,
        scheduled_at: scheduled,
      };
      await notificationsApi.create(submitData);
      toast.success(sendNow ? 'Уведомление отправлено' : 'Уведомление запланировано');
      navigate('/notifications');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось создать уведомление'));
    } finally {
      setIsLoading(false);
    }
  };

  const recipientsText = preview && !previewLoading ? fmt(preview.estimated_recipients) : null;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Button variant="link" asChild className="self-start">
          <Link to="/notifications"><ArrowLeft aria-hidden />Все уведомления</Link>
        </Button>
        <PageHeader
          title="Новое уведомление"
          subtitle="Сообщение появится в приложении у каждого получателя; push — дополнительный канал"
        />
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <form
          className="flex min-w-0 flex-[1_1_520px] flex-col gap-3"
          noValidate
          onSubmit={(e) => { e.preventDefault(); void handleSubmit(); }}
        >
          <Card role="region" aria-labelledby="n-msg" className="flex flex-col gap-3.5 p-[18px]">
            <b id="n-msg" className="font-display text-[17px] font-bold">Сообщение</b>
            <div className="flex flex-col gap-1.5">
              <span id="n-type" className="text-[13px] font-semibold">Тип</span>
              <SegmentedControl
                ariaLabel="Тип уведомления"
                value={formData.type}
                onChange={(type) => setFormData((prev) => ({ ...prev, type }))}
                options={TYPE_OPTIONS}
                className="flex-wrap self-start"
              />
              <span className="text-xs text-text-2">
                Пользователь может выключить отдельные типы в настройках уведомлений — для аудитории «По настройкам» это учитывается
              </span>
            </div>
            <FormField
              label="Заголовок"
              required
              error={titleError}
              hint={<span className="tabular-nums">{formData.title.length} симв.</span>}
            >
              <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Mashg‘ulotni davom ettiramizmi?" />
            </FormField>
            <FormField
              label="Текст"
              required
              error={bodyError}
              hint={
                <>
                  <span className="tabular-nums">{formData.body.length} симв.</span> Один вариант текста: бэкенд пока не принимает
                  переводы, все получат сообщение как есть, независимо от языка приложения
                </>
              }
            >
              <Textarea value={formData.body} onChange={(e) => setFormData({ ...formData, body: e.target.value })} rows={3} placeholder="Текст уведомления" />
            </FormField>
          </Card>

          <Card role="region" aria-labelledby="n-aud" className="flex flex-col gap-3.5 p-[18px]">
            <b id="n-aud" className="font-display text-[17px] font-bold">Кому и куда</b>
            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
              <legend className="mb-1.5 text-[13px] font-semibold">Аудитория</legend>
              <div className="flex flex-col gap-1.5">
                {AUDIENCES.map((a) => {
                  const checked = audience.audience_type === a.value;
                  // Оценку даёт только preview выбранной аудитории; breakdown по сегментам с ней не сходится
                  // (считается по-другому), поэтому для остальных вариантов число не показываем.
                  const count = checked && preview && !previewLoading ? preview.estimated_recipients : null;
                  return (
                    <label
                      key={a.value}
                      className={cn(
                        'flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-xl border px-3.5 py-2',
                        checked ? 'border-accent bg-accent-soft shadow-[inset_0_0_0_1px_var(--accent)]' : 'border-border hover:bg-surface-alt',
                      )}
                    >
                      <span className="flex items-center gap-2.5">
                        <input
                          type="radio"
                          name="audience"
                          value={a.value}
                          checked={checked}
                          onChange={() => setAudience({ audience_type: a.value })}
                          className="size-4 accent-text"
                        />
                        <span className="flex flex-col">
                          <span className="font-semibold">{a.label}</span>
                          {a.hint && <span className="text-xs text-text-2">{a.hint}</span>}
                        </span>
                      </span>
                      {count != null && <span className="text-[13px] tabular-nums text-text-2">{fmt(count)} человек</span>}
                    </label>
                  );
                })}
              </div>
              {audience.audience_type === AudienceType.BY_ACTIVITY && (
                <div className="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField label="Заходили за последние, дней" hint="Пусто — не учитывать">
                    <Input type="number" min={0} inputMode="numeric" placeholder="30"
                      value={audience.activity_filters?.days_active ?? ''} onChange={(e) => setActivity('days_active', e.target.value)} />
                  </FormField>
                  <FormField label="Не заходили, дней и дольше" hint="Пусто — не учитывать">
                    <Input type="number" min={0} inputMode="numeric" placeholder="7"
                      value={audience.activity_filters?.days_inactive ?? ''} onChange={(e) => setActivity('days_inactive', e.target.value)} />
                  </FormField>
                </div>
              )}
            </fieldset>
            <FormField
              label="По нажатию открыть"
              hint={
                <>Только экраны, которые приложение умеет открывать.{deepLink && <> Ссылка: <code>{deepLink}</code></>}</>
              }
            >
              <select
                value={deepLinkScreen}
                onChange={(e) => setDeepLinkScreen(e.target.value)}
                className="h-10 w-full rounded-xl border border-border bg-surface px-3 text-sm text-text outline-none focus-visible:border-accent-text focus-visible:ring-3 focus-visible:ring-accent/35"
              >
                <option value="">Ничего — только карточка уведомления</option>
                {DEEP_LINK_SCREENS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </FormField>
          </Card>

          <Card role="region" aria-labelledby="n-send" className="flex flex-col gap-3.5 p-[18px]">
            <b id="n-send" className="font-display text-[17px] font-bold">Отправка</b>
            <fieldset className="m-0 flex flex-wrap items-end gap-x-5 gap-y-3 border-0 p-0">
              <legend className="mb-1.5 text-[13px] font-semibold">Когда отправить</legend>
              <label className="flex h-10 cursor-pointer items-center gap-2">
                <input type="radio" name="when" checked={sendNow} onChange={() => setSendNow(true)} className="size-4 accent-text" />
                Сейчас
              </label>
              <label className="flex h-10 cursor-pointer items-center gap-2">
                <input type="radio" name="when" checked={!sendNow} onChange={() => setSendNow(false)} className="size-4 accent-text" />
                Запланировать
              </label>
              <FormField label="Дата и время по Ташкенту" error={scheduleError} className="min-w-[220px]">
                <Input type="datetime-local" disabled={sendNow} min={toTashkentInput(new Date())}
                  value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
              </FormField>
            </fieldset>
            <div className="flex flex-col gap-3 border-t border-border-soft pt-3.5">
              <ToggleRow
                label="Рекламное уведомление"
                hint="Пометить как рекламу"
                checked={!!formData.is_sponsored}
                onChange={(is_sponsored) => setFormData({ ...formData, is_sponsored })}
              />
              <ToggleRow
                label="Премиум-уведомление"
                hint="Пометить как премиум"
                checked={!!formData.is_premium}
                onChange={(is_premium) => setFormData({ ...formData, is_premium })}
              />
              <FormField label="Приоритет" hint="От 1 до 10, по умолчанию 5" className="max-w-[200px]">
                <Input type="number" min={1} max={10} value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) || 5 })} />
              </FormField>
            </div>
          </Card>

          <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-card border border-border bg-surface px-[18px] py-3 shadow-pop">
            <span className="text-[13px] text-text-2">Перед отправкой покажем итог и попросим подтвердить</span>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? <Loader2 className="animate-spin" aria-hidden /> : sendNow ? <Send aria-hidden /> : <CalendarClock aria-hidden />}
              {sendNow
                ? recipientsText ? `Отправить ${recipientsText} получателям` : 'Отправить сейчас'
                : 'Запланировать'}
            </Button>
          </div>
        </form>

        <aside aria-label="Предпросмотр" className="sticky top-5 flex min-w-0 max-w-[380px] flex-[1_1_340px] flex-col gap-3">
          <Card className="flex flex-col gap-3.5 p-[18px]">
            <div className="flex items-center justify-between">
              <b className="font-display text-[17px] font-bold">Предпросмотр</b>
              <span className="text-xs text-text-2">Интерфейс: O‘zbekcha</span>
            </div>
            <PushPreview title={formData.title} body={formData.body} />
            <span className="text-xs font-semibold uppercase tracking-[0.05em] text-text-2">В приложении</span>
            <div className="rounded-2xl bg-bg p-2.5">
              <div className="flex flex-col gap-1.5 rounded-[14px] bg-surface px-3.5 py-3 shadow-[inset_0_0_0_2px_var(--accent)]">
                <span className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold">
                    <span className="size-2 rounded-full bg-accent" aria-hidden />
                    {APP_TYPE_LABELS_UZ[formData.type]} · yangi
                  </span>
                  <span className="text-xs text-text-2">hozir</span>
                </span>
                <b className="break-words font-display text-[15px]">{formData.title || 'Заголовок'}</b>
                <span className="whitespace-pre-line break-words text-[13px] leading-[1.4] text-text-2">{formData.body || 'Текст уведомления'}</span>
                {deepLink && (
                  <span className="mt-1 grid h-9 place-items-center rounded-xl bg-inverse text-[13px] font-bold text-on-inverse">
                    {APP_OPEN_LABEL_UZ}
                  </span>
                )}
              </div>
            </div>
          </Card>

          <Card role="region" aria-label="Охват" aria-live="polite" className="flex flex-col gap-2.5 px-[18px] py-4">
            {previewError ? (
              <ErrorState compact message={previewError} onRetry={() => runPreview(formData.type, formData.audience_filters)} />
            ) : (
              <>
                <div className="flex items-baseline justify-between">
                  <span className="text-text-2">Получатели</span>
                  {previewLoading || !preview
                    ? <Skeleton className="h-7 w-20" />
                    : <b className="font-display text-2xl font-extrabold tabular-nums">{fmt(preview.estimated_recipients)}</b>}
                </div>
                <div className="flex justify-between">
                  <span className="text-text-2">Получат push</span>
                  {previewLoading || !preview
                    ? <Skeleton className="h-5 w-14" />
                    : <span className="font-semibold tabular-nums">≈ {fmt(preview.estimated_push_recipients ?? 0)}</span>}
                </div>
                <div className="flex justify-between">
                  <span className="text-text-2">Устройств</span>
                  {previewLoading || !preview
                    ? <Skeleton className="h-5 w-14" />
                    : <span className="font-semibold tabular-nums">{fmt(preview.estimated_devices)}</span>}
                </div>
              </>
            )}
            <span className="text-xs leading-[1.45] text-text-2">
              Push уходит только тем, кто разрешил уведомления; остальные увидят сообщение в приложении.
              {previewAt && !previewLoading && <> Пересчитано в <span className="tabular-nums">{formatTashkentTime(previewAt)}</span></>}
            </span>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: (v: boolean) => void }) {
  const id = `t-${label.replace(/\s+/g, '-')}`;
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="flex flex-col">
        <label htmlFor={id} className="text-[13px] font-semibold">{label}</label>
        <span className="text-xs text-text-2">{hint}</span>
      </span>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

const UZ_WEEKDAYS = ['Yakshanba', 'Dushanba', 'Seshanba', 'Chorshanba', 'Payshanba', 'Juma', 'Shanba'];
const UZ_MONTHS = ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr'];

/** Экран блокировки телефона с push-карточкой — как её покажет система. */
function PushPreview({ title, body }: { title: string; body: string }) {
  const now = new Date();
  // Своё форматирование: локали uz в браузерах нет или она неполная («M10 31, Sat»).
  const t = new Date(now.getTime() + 5 * 60 * 60 * 1000); // Ташкент, UTC+5 без перехода на летнее время
  const date = `${UZ_WEEKDAYS[t.getUTCDay()]}, ${t.getUTCDate()}-${UZ_MONTHS[t.getUTCMonth()]}`;
  return (
    <div className="flex flex-col gap-3 rounded-[26px] bg-hero px-3 pb-3.5 pt-[18px] text-on-hero">
      <span className="flex flex-col text-center">
        <span className="text-xs text-on-hero-2">{date}</span>
        <b className="font-display text-[40px] font-bold leading-[1.1] tabular-nums">{formatTashkentTime(now)}</b>
      </span>
      <div className="flex items-start gap-2.5 rounded-2xl bg-surface px-3 py-2.5 text-text">
        <span className="grid size-[34px] shrink-0 place-items-center rounded-[9px] bg-accent text-on-accent" aria-hidden>
          <Bell className="size-[18px]" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex justify-between gap-2 text-[11px] text-text-2">
            <span className="uppercase tracking-[0.04em]">{BRAND.name}</span>
            <span>hozir</span>
          </span>
          <b className="break-words text-[13px]">{title || 'Заголовок'}</b>
          <span className="line-clamp-4 break-words text-[13px] leading-[1.35]">{body || 'Текст уведомления'}</span>
        </span>
      </div>
    </div>
  );
}
